"use client";

import { useEffect, useRef, useState } from "react";
import { useLocale } from "./LocaleProvider";

const copy = {
  en: { stanmore: "Call Stanmore", walton: "Call Walton-on-Thames", label: "Call us" },
  ro: { stanmore: "Sună Stanmore", walton: "Sună Walton-on-Thames", label: "Sună-ne" },
} as const;

type Props = {
  onOpenChange?: (open: boolean) => void;
  suspend?: boolean;
};

export default function CallButtonsStack({ onOpenChange, suspend = false }: Props) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const t = copy[useLocale()];

  const STANMORE_TEL = "+442033016323";
  const WALTON_TEL = "+441932988460"; // ✅ real

  // If we suspend (user scrolling) -> close menu
  useEffect(() => {
    if (suspend) setOpen(false);
  }, [suspend]);

  useEffect(() => {
    onOpenChange?.(open);
  }, [open, onOpenChange]);

  useEffect(() => {
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (!open) return;
      if (!rootRef.current) return;
      const t = e.target as Node | null;
      if (t && !rootRef.current.contains(t)) setOpen(false);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown, { passive: true });
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
    };
  }, [open]);

  const hidden = !!suspend;

  return (
    <div
      ref={rootRef}
      className={[
        "fixed right-5 z-[9999] md:hidden",
        // sit above WhatsApp
        "bottom-[92px]",
        hidden
          ? "opacity-0 pointer-events-none translate-y-2"
          : "opacity-100 pointer-events-auto translate-y-0",
        "transition-all duration-300 ease-out",
      ].join(" ")}
    >
      {/* Panel */}
      <div
        className={[
          "absolute right-0 bottom-[68px]",
          open
            ? "opacity-100 scale-100 translate-y-0 pointer-events-auto"
            : "opacity-0 scale-95 translate-y-2 pointer-events-none",
          "transition-all duration-200 ease-out origin-bottom-right",
        ].join(" ")}
      >
        <div className="min-w-[240px] rounded-2xl border border-white/10 bg-black/75 backdrop-blur-xl shadow-2xl p-2">
          <a
            href={`tel:${STANMORE_TEL}`}
            className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm text-white hover:bg-white/5 transition"
            onClick={() => setOpen(false)}
          >
            <span className="font-medium">{t.stanmore}</span>
            <span className="text-white/60 text-xs">{STANMORE_TEL}</span>
          </a>

          <a
            href={`tel:${WALTON_TEL}`}
            className="flex items-center justify-between gap-3 rounded-xl px-3 py-2 text-sm text-white hover:bg-white/5 transition"
            onClick={() => setOpen(false)}
          >
            <span className="font-medium">{t.walton}</span>
            <span className="text-white/60 text-xs">{WALTON_TEL}</span>
          </a>
        </div>
      </div>

      {/* Main Call button (round like WhatsApp) */}
      <button
        type="button"
        aria-label={t.label}
        onClick={() => setOpen((v) => !v)}
        className={[
          "h-14 w-14 rounded-full",
          "bg-emerald-400 text-black",
          "shadow-[0_10px_40px_rgba(0,0,0,0.55)] border border-white/10",
          "grid place-items-center",
          "transition-transform duration-150",
          "hover:scale-110 active:scale-[0.96]",
        ].join(" ")}
        title={t.label}
      >
        {/* phone icon */}
        <svg viewBox="0 0 24 24" className="h-7 w-7" aria-hidden="true">
          <path
            fill="currentColor"
            d="M6.62 10.79a15.05 15.05 0 0 0 6.59 6.59l2.2-2.2a1 1 0 0 1 .95-.27c1.04.25 2.16.38 3.31.38a1 1 0 0 1 1 1V20a1 1 0 0 1-1 1C10.07 21 3 13.93 3 5a1 1 0 0 1 1-1h3.52a1 1 0 0 1 1 1c0 1.15.13 2.27.38 3.31a1 1 0 0 1-.27.95l-2.01 2.53Z"
          />
        </svg>
      </button>
    </div>
  );
}
